import { Injectable, inject, signal, computed, effect, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ElevatorStateService } from './elevator-state.service';
import { MapTourService } from './map-tour.service';

@Injectable({
  providedIn: 'root'
})
export class MapSoundService {
  private platformId = inject(PLATFORM_ID);
  private stateService = inject(ElevatorStateService);
  private mapTourService = inject(MapTourService);

  private audioCtx: AudioContext | null = null;
  private soundInterval: ReturnType<typeof setInterval> | null = null;
  private toneStep = 0;

  /** User preference: whether map alert sound is enabled (stored in localStorage) */
  readonly isSoundEnabled = signal<boolean>(this.loadSoundPreference());

  /** Whether any elevator has an active ERROR, emergency call, or active alert */
  readonly hasEmergency = computed(() => {
    const elevators = this.stateService.elevators ? this.stateService.elevators() : [];
    const hasError = elevators.some(e => e.status === 'ERROR');
    const hasEmergencyCall = elevators.some(
      e => e.emergencyCallStatus === 'CALL_ATTEMPT' || e.emergencyCallStatus === 'IN_CALL'
    );
    const activeAlerts = typeof this.stateService.activeAlerts === 'function' ? this.stateService.activeAlerts() : [];
    const hasActiveAlerts = activeAlerts.length > 0;

    return hasError || hasEmergencyCall || hasActiveAlerts;
  });

  /** Only play when on Map Page, sound is enabled, and an emergency condition is present */
  readonly shouldPlay = computed(() => {
    return this.mapTourService.isMapPage() && this.isSoundEnabled() && this.hasEmergency();
  });

  /** Current playing status */
  readonly isPlaying = signal<boolean>(false);

  constructor() {
    effect(() => {
      const play = this.shouldPlay();
      if (play) {
        this.startAlarm();
      } else {
        this.stopAlarm();
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      const unlock = () => {
        if (this.audioCtx && this.audioCtx.state === 'suspended') {
          this.audioCtx.resume();
        }
      };
      window.addEventListener('click', unlock, { passive: true });
      window.addEventListener('keydown', unlock, { passive: true });
    }
  }

  private loadSoundPreference(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    try {
      const saved = localStorage.getItem('rms_map_sound_enabled');
      return saved === null ? false : saved === 'true';
    } catch {
      return false;
    }
  }

  public toggleSound() {
    const next = !this.isSoundEnabled();
    this.isSoundEnabled.set(next);

    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem('rms_map_sound_enabled', String(next));
      } catch {}
      if (next && this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
    }
  }

  private initAudio() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.audioCtx) {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        this.audioCtx = new AudioCtxClass();
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
  }

  private startAlarm() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.isPlaying()) return;

    this.initAudio();
    if (!this.audioCtx) return;

    this.isPlaying.set(true);

    const playBeep = () => {
      if (!this.isPlaying() || !this.audioCtx) return;
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      try {
        const now = this.audioCtx.currentTime;
        const osc = this.audioCtx.createOscillator();
        const gain = this.audioCtx.createGain();

        // Alternating dual-tone alert siren (880Hz / 659.25Hz)
        const freq = this.toneStep % 2 === 0 ? 880 : 659.25;
        this.toneStep++;

        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now);

        // Envelope: 250ms duration with subtle attack & decay
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.12, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

        osc.connect(gain);
        gain.connect(this.audioCtx.destination);

        osc.start(now);
        osc.stop(now + 0.24);
      } catch (err) {
        console.warn('Map alarm sound synthesis error:', err);
      }
    };

    // Immediate first beep
    playBeep();
    // Subsequent beeps every 450ms
    this.soundInterval = setInterval(playBeep, 450);
  }

  private stopAlarm() {
    if (this.soundInterval) {
      clearInterval(this.soundInterval);
      this.soundInterval = null;
    }
    this.toneStep = 0;
    this.isPlaying.set(false);
  }
}
